import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { take, takeUntil } from 'rxjs/operators';
import { AvailableLangs, TranslocoService } from '@ngneat/transloco';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { LanguagesUtils } from '../../../core/transloco/languages.utils';
import { ObjectRef } from 'app/modules/shared/models/objectRef';
import { LanguageEnum } from 'app/modules/shared/enums/languageEnum';
import { Subject } from 'rxjs';
import { User } from 'app/core/user/user.types';
import { UserService } from 'app/core/user/user.service';
import { SpinnerService } from 'app/modules/shared/services/spinner.service';
import { NotificationService } from 'app/modules/shared/services/notification.service';
import { NgTemplateOutlet } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { UserUtil } from 'app/modules/shared/utils/user.util';
import { PartnerUserService } from 'app/modules/partner/services/partner-user.service';
import { TeamUserService } from 'app/modules/team/services/team-user.service';
import { AffiliatePortalService } from 'app/modules/affiliate/services/affiliate-portal.service';

@Component({
    selector: 'languages',
    templateUrl: './languages.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'languages',
    standalone: true,
    imports: [MatButtonModule, MatMenuModule, NgTemplateOutlet],
})
export class LanguagesComponent implements OnInit, OnDestroy {
    availableLangs: AvailableLangs;
    activeLang: string;
    flagCodes: any;
    user: User;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _userService: UserService,
        private _fuseNavigationService: FuseNavigationService,
        private _translocoService: TranslocoService,
        private _spinnerService: SpinnerService,
        private _notificationService: NotificationService,
        private _partnerUserService: PartnerUserService,
        private _teamUserService: TeamUserService,
        private _affiliatePortalService: AffiliatePortalService
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Get the available languages from transloco
        this.availableLangs = this._translocoService.getAvailableLangs();

        // Subscribe to language changes
        this._translocoService.langChanges$.subscribe((activeLang) => {
            // Get the active lang
            this.activeLang = activeLang;

            // Update the navigation
            this._updateNavigation(activeLang);
        });

        // Generate flag codes based on available languages from transloco configuration
        this.flagCodes = this._generateFlagCodes();

        this._userService.user$
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe((user: User) => {
            this.user = user;
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }
    

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Set the active lang
     *
     * @param lang
     */
    setActiveLang(lang: string): void {
        // Set the active lang
        this._translocoService.setActiveLang(lang);

        // Save the active lang in navigator
        LanguagesUtils.saveLangInLocal(lang);
        let objectTmp = new ObjectRef(lang, LanguageEnum[lang.toUpperCase()]);
        this._spinnerService.show();
        if (UserUtil.isPartnerUser(this.user)) {
            this._partnerUserService.updateUserLanguage(this.user.partnerId, this.user.id, objectTmp)
                .subscribe((res) => {
                    this._spinnerService.hide();
                    this._notificationService.success(res.message);
                }, (err) => {
                    this._spinnerService.hide();
                    // TODO : To delete after checking errors are running fine
// this._notificationService.error(err?.error?.message);
                });
        } else if (UserUtil.isTeamUser(this.user)) {
            this._teamUserService.updateUserLanguage(this.user.id, objectTmp)
            .subscribe((res) => {
                this._spinnerService.hide();
                this._notificationService.success(res.message);
            }, (err) => {
                this._spinnerService.hide();
                // TODO : To delete after checking errors are running fine
// this._notificationService.error(err?.error?.message);
            });
        } else if (UserUtil.isAffiliateUser(this.user)) {
            this._affiliatePortalService.updateLanguage(lang)
                .subscribe({
                    next: () => {
                        this._spinnerService.hide();
                    },
                    error: () => {
                        this._spinnerService.hide();
                    },
                });
        } else {
            // Unauthenticated context (shouldn't happen) — just stop the spinner.
            this._spinnerService.hide();
        }
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Generate flag codes based on available languages from transloco configuration
     * Maps language codes to their corresponding flag country codes
     * 
     * @private
     */
    private _generateFlagCodes(): { [key: string]: string } {
        const flagMapping: { [key: string]: string } = {};
        
        // Get available languages from transloco and map them to flag codes
        const availableLangs = this._translocoService.getAvailableLangs();
        
        if (Array.isArray(availableLangs)) {
            availableLangs.forEach((lang: any) => {
                const langId = typeof lang === 'string' ? lang : lang.id;
                
                // Map language codes to flag country codes
                switch (langId) {
                    case 'en':
                        flagMapping[langId] = 'uk';
                        break;
                    case 'fr':
                        flagMapping[langId] = 'fr';
                        break;
                    case 'it':
                        flagMapping[langId] = 'it';
                        break;
                    case 'pt':
                        flagMapping[langId] = 'pt';
                        break;
                    case 'es':
                        flagMapping[langId] = 'es';
                        break;
                    case 'de':
                        flagMapping[langId] = 'de';
                        break;
                    case 'tr':
                        flagMapping[langId] = 'tr';
                        break;
                    case 'ar':
                        flagMapping[langId] = 'sa'; // Using Saudi Arabia flag for Arabic
                        break;
                    case 'ja':
                        flagMapping[langId] = 'jp';
                        break;
                    case 'ru':
                        flagMapping[langId] = 'ru';
                        break;
                    case 'el':
                        flagMapping[langId] = 'gr'; // Greek -> Greece flag
                        break;
                    case 'th':
                        flagMapping[langId] = 'th'; // Thai -> Thailand flag
                        break;
                    case 'hi':
                        flagMapping[langId] = 'in'; // Hindi -> India flag
                        break;
                    case 'ro':
                        flagMapping[langId] = 'ro'; // Romanian -> Romania flag
                        break;
                    case 'he':
                        flagMapping[langId] = 'il'; // Hebrew -> Israel flag
                        break;
                    default:
                        // If no specific flag mapping is found, use the language code as country code
                        flagMapping[langId] = langId;
                        break;
                }
            });
        }
        
        return flagMapping;
    }

    /**
     * Update the navigation
     *
     * @param lang
     * @private
     */
    private _updateNavigation(lang: string): void {
        // For the demonstration purposes, we will only update the Dashboard names
        // from the navigation but you can do a full swap and change the entire
        // navigation data.
        //
        // You can import the data from a file or request it from your backend,
        // it's up to you.

        // Get the component -> navigation data -> item
        const navComponent =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                'mainNavigation'
            );

        // Return if the navigation component does not exist
        if (!navComponent) {
            return null;
        }

        // Get the flat navigation data
        const navigation = navComponent.navigation;
       
        navigation.map((nav) => {
            const item = this._fuseNavigationService.getItem(nav.id, navigation);
            if (item)
            {
                let path = 'navigation.' + nav.id;
                this._translocoService.selectTranslate(path + '.title').pipe(take(1))
                    .subscribe((translation) => {
                        item.title = translation;
                        navComponent.refresh();
                });
                this.updateChildrenItem(nav, item, navComponent, navigation, path);
            }
        })
    }

    updateChildrenItem(nav: any, item: any, navComponent: any, navigation: any, path: string) {
        if (item.children && item.children.length > 0) {
            item.children.map((child) => {
                const itemChild = this._fuseNavigationService.getItem(child.id, navigation);
                let newPath = path + '.' + child.id;
                this._translocoService.selectTranslate(newPath + '.title').pipe(take(1))
                .subscribe((translation) => {
                    itemChild.title = translation;
                    navComponent.refresh();
                });
                this.updateChildrenItem(nav, child, navComponent, navigation, newPath);
            });
        }
    }
}
